import { api } from "./api";
import { loadPageFont } from "./fonts";
import { PAGE_COUNT, type PageData } from "./types";

// Page data is cached for the session so that turning back and forth, and
// turning onto a prefetched neighbour, needs no network round trip. Entries
// expire so edits made by other admins still show up eventually.
const TTL_MS = 5 * 60 * 1000;
const cache = new Map<number, { at: number; data: Promise<PageData> }>();

export const isValidPage = (page: number) =>
	Number.isInteger(page) && page >= 1 && page <= PAGE_COUNT;

declare global {
	interface Window {
		/** Started by the inline script in index.html on a direct page visit. */
		__hifzPrefetch?: { page: number; data: Promise<PageData | null> };
	}
}

function takeEarlyPrefetch(page: number): Promise<PageData> | null {
	const early = window.__hifzPrefetch;
	if (!early || early.page !== page) return null;
	window.__hifzPrefetch = undefined;
	return early.data.then(
		(data) => data ?? api<PageData>(`/api/pages/${page}`),
		() => api<PageData>(`/api/pages/${page}`),
	);
}

export function getPageData(page: number): Promise<PageData> {
	const hit = cache.get(page);
	if (hit && Date.now() - hit.at < TTL_MS) return hit.data;

	const data = takeEarlyPrefetch(page) ?? api<PageData>(`/api/pages/${page}`);
	cache.set(page, { at: Date.now(), data });
	data.catch(() => cache.delete(page));
	return data;
}

export function invalidatePage(page: number) {
	cache.delete(page);
}

/**
 * Resolves once both the page's words and its mushaf font are ready, so the
 * page can be shown fully rendered straight away. A font failure does not
 * block the page; the view falls back to its own font loading.
 */
export async function loadPage(page: number): Promise<PageData> {
	const [data] = await Promise.all([getPageData(page), loadPageFont(page).catch(() => {})]);
	return data;
}

/** Warms data and fonts around the current page, favouring reading direction. */
export function prefetchAround(page: number) {
	const idle =
		(window as Window & { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback ??
		((cb: () => void) => setTimeout(cb, 200));

	idle(() => {
		for (const next of [page + 1, page - 1, page + 2]) {
			if (!isValidPage(next)) continue;
			getPageData(next).catch(() => {});
			loadPageFont(next).catch(() => {});
		}
	});
}
